/**
 * MaterialPickerModal — modal disciplinado para añadir material a una OT.
 *
 * Cumple las nuevas reglas del backend:
 *   1) Buscar repuesto en inventario por SKU/nombre/EAN (autocomplete) ─► enviar `repuesto_id`.
 *   2) Si no existe, "Crear nuevo" exige nombre, sku único, categoría y proveedor.
 *
 * El backend rechaza payloads sin estos datos (400/409). Aquí validamos primero
 * para evitar el roundtrip y mostramos el error del servidor si llega.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Search, Plus, Package, AlertCircle } from 'lucide-react';
import { repuestosAPI, proveedoresAPI, ordenesAPI } from '../lib/api';

const CATEGORIAS_DEFAULT = [
  'pantallas', 'baterias', 'tapas', 'placas', 'flex', 'altavoces',
  'microfonos', 'camaras', 'botones', 'conectores', 'accesorios', 'otros',
];

export default function MaterialPickerModal({
  open,
  onClose,
  ordenId,
  onAdded,                  // (response.data) => void
  addedByTechnician = false,
  apiAñadir,                // función (ordenId, payload) → axios promise; default usa ordenesAPI.añadirMaterial
  prefillRepuesto = null,   // si se pasa, abre directamente en tab "Buscar" con este repuesto seleccionado
  disableCreateTab = false, // útil para técnicos: oculta el tab "Crear nuevo"
}) {
  const [tab, setTab] = useState('buscar');
  const [submitting, setSubmitting] = useState(false);

  // ── Tab "Buscar" ─────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const debounceRef = useRef(null);

  // ── Tab "Crear" ───────────────────────────────────────────────────────────
  const [proveedores, setProveedores] = useState([]);
  const [loadingProv, setLoadingProv] = useState(false);
  const [nuevo, setNuevo] = useState({
    nombre: '', sku: '', categoria: 'pantallas', proveedor_id: '', proveedor_nombre: '',
    precio_compra: '', precio_venta: '', sku_proveedor: '', ean: '',
    stock_inicial: 0, stock_minimo: 1,
  });
  const [cantidadNuevo, setCantidadNuevo] = useState(1);

  // Cargar proveedores 1× al abrir (con logging y fallback ante errores)
  useEffect(() => {
    if (!open) return;
    setLoadingProv(true);
    proveedoresAPI.listar()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setProveedores(list);
        if (list.length === 0) {
          console.warn('[MaterialPicker] /api/proveedores devolvió 0 elementos');
        }
      })
      .catch((err) => {
        console.error('[MaterialPicker] Error cargando proveedores:', err);
        setProveedores([]);
      })
      .finally(() => setLoadingProv(false));
  }, [open]);

  // Búsqueda debounced
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults([]); return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await repuestosAPI.buscarRapido(query, null, 20);
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setResults(list);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Reset al abrir/cerrar y aplicar prefill si se proporciona
  useEffect(() => {
    if (!open) {
      setTab('buscar'); setQuery(''); setResults([]); setSelected(null);
      setCantidad(1); setCantidadNuevo(1);
      setNuevo({
        nombre: '', sku: '', categoria: 'pantallas', proveedor_id: '', proveedor_nombre: '',
        precio_compra: '', precio_venta: '', sku_proveedor: '', ean: '',
        stock_inicial: 0, stock_minimo: 1,
      });
    } else if (prefillRepuesto) {
      setTab('buscar');
      setSelected(prefillRepuesto);
      setQuery(prefillRepuesto.nombre || prefillRepuesto.sku || '');
    }
  }, [open, prefillRepuesto]);

  const submitBuscar = async () => {
    if (!selected) {
      toast.error('Selecciona un repuesto del inventario');
      return;
    }
    if (cantidad < 1) {
      toast.error('La cantidad debe ser ≥ 1');
      return;
    }
    setSubmitting(true);
    try {
      const fn = apiAñadir || ordenesAPI.añadirMaterial;
      const r = await fn(ordenId, {
        repuesto_id: selected.id,
        cantidad,
        añadido_por_tecnico: addedByTechnician,
      });
      _handleAddedResponse(r.data);
      onAdded?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al añadir material');
    } finally {
      setSubmitting(false);
    }
  };

  const submitCrear = async () => {
    const faltantes = [];
    if (!nuevo.nombre.trim()) faltantes.push('nombre');
    if (!nuevo.sku.trim()) faltantes.push('sku');
    if (!nuevo.categoria.trim()) faltantes.push('categoría');
    if (!nuevo.proveedor_id && !(nuevo.proveedor_nombre || '').trim()) faltantes.push('proveedor');
    if (faltantes.length) {
      toast.error(`Faltan campos obligatorios: ${faltantes.join(', ')}`);
      return;
    }
    if (cantidadNuevo < 1) {
      toast.error('La cantidad debe ser ≥ 1');
      return;
    }
    setSubmitting(true);
    try {
      const fn = apiAñadir || ordenesAPI.añadirMaterial;
      const r = await fn(ordenId, {
        cantidad: cantidadNuevo,
        añadido_por_tecnico: addedByTechnician,
        nuevo_repuesto: {
          nombre: nuevo.nombre.trim(),
          sku: nuevo.sku.trim(),
          categoria: nuevo.categoria.trim(),
          proveedor_id: nuevo.proveedor_id || null,
          proveedor_nombre: nuevo.proveedor_id ? null : (nuevo.proveedor_nombre || '').trim() || null,
          precio_compra: parseFloat(nuevo.precio_compra) || 0,
          precio_venta: parseFloat(nuevo.precio_venta) || 0,
          sku_proveedor: nuevo.sku_proveedor.trim() || null,
          ean: nuevo.ean.trim() || null,
          stock_inicial: parseInt(nuevo.stock_inicial, 10) || 0,
          stock_minimo: parseInt(nuevo.stock_minimo, 10) || 1,
        },
      });
      _handleAddedResponse(r.data);
      onAdded?.(r.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Error al crear repuesto');
    } finally {
      setSubmitting(false);
    }
  };

  const _handleAddedResponse = (data) => {
    if (data?.peticion_compra) {
      const pc = data.peticion_compra;
      toast.success('Material añadido', {
        description: `Sin stock suficiente. Se ha creado una petición de compra (${pc.cantidad_solicitada} ud, urgencia ${pc.urgencia}).`,
      });
    } else if (data?.repuesto_creado) {
      toast.success('Material añadido y repuesto creado en inventario');
    } else {
      toast.success('Material añadido');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="material-picker-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" /> Añadir material a la orden
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className={`grid w-full ${disableCreateTab ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <TabsTrigger value="buscar" data-testid="tab-buscar-inventario">
              <Search className="w-4 h-4 mr-1" /> Buscar en inventario
            </TabsTrigger>
            {!disableCreateTab && (
              <TabsTrigger value="crear" data-testid="tab-crear-nuevo">
                <Plus className="w-4 h-4 mr-1" /> Crear nuevo
              </TabsTrigger>
            )}
          </TabsList>

          {/* ── Buscar ─────────────────────────────────────────────────── */}
          <TabsContent value="buscar" className="space-y-3 mt-3">
            <div>
              <Label>Buscar por nombre, SKU o EAN</Label>
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ej. iPhone 12 pantalla…"
                data-testid="input-buscar-repuesto"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
              {searching && <div className="p-3 text-sm text-muted-foreground">Buscando…</div>}
              {!searching && query.length >= 2 && results.length === 0 && (
                <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  No se encontró ningún repuesto. Crea uno nuevo en la otra pestaña.
                </div>
              )}
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelected(r)}
                  data-testid={`result-${r.id}`}
                  className={`w-full text-left p-2 hover:bg-slate-50 transition-colors ${
                    selected?.id === r.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">{r.nombre}</div>
                    <Badge variant={r.stock > 0 ? 'default' : 'destructive'} className="ml-2">
                      Stock: {r.stock ?? 0}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                    <span className="font-mono">{r.sku || '—'}</span>
                    {r.categoria && <span>· {r.categoria}</span>}
                    {r.precio_venta != null && <span>· {r.precio_venta} €</span>}
                  </div>
                </button>
              ))}
            </div>
            {selected && (
              <div className="flex items-end gap-3 p-3 bg-slate-50 rounded-md">
                <div className="flex-1">
                  <div className="text-sm font-medium">{selected.nombre}</div>
                  <div className="text-xs text-muted-foreground">
                    Stock disponible: <strong>{selected.stock ?? 0}</strong>
                    {selected.stock < cantidad && (
                      <span className="ml-2 text-amber-600">
                        ⚠ Sin stock → se creará petición de compra automática
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Cantidad</Label>
                  <Input
                    type="number" min={1}
                    value={cantidad}
                    onChange={(e) => setCantidad(parseInt(e.target.value, 10) || 1)}
                    className="w-20"
                    data-testid="input-cantidad-buscar"
                  />
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Crear ──────────────────────────────────────────────────── */}
          <TabsContent value="crear" className="space-y-3 mt-3">
            <div className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded-md p-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 text-blue-700 flex-shrink-0" />
              <span>El repuesto se creará en inventario con <strong>stock 0</strong> y se generará una petición de compra automática.</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nombre del repuesto *</Label>
                <Input
                  value={nuevo.nombre}
                  onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
                  placeholder="Pantalla iPhone 12 OLED"
                  data-testid="input-nuevo-nombre"
                />
              </div>
              <div>
                <Label>SKU interno *</Label>
                <Input
                  value={nuevo.sku}
                  onChange={(e) => setNuevo({ ...nuevo, sku: e.target.value.toUpperCase() })}
                  placeholder="PAN-IP12-OLED"
                  className="font-mono"
                  data-testid="input-nuevo-sku"
                />
              </div>
              <div>
                <Label>Categoría *</Label>
                <Select
                  value={nuevo.categoria}
                  onValueChange={(v) => setNuevo({ ...nuevo, categoria: v })}
                >
                  <SelectTrigger data-testid="select-nuevo-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS_DEFAULT.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Proveedor *</Label>
                {loadingProv ? (
                  <div className="text-xs text-muted-foreground py-2">Cargando proveedores…</div>
                ) : proveedores.length === 0 ? (
                  <div className="space-y-2">
                    <Input
                      placeholder="Nombre del proveedor (no hay proveedores configurados)"
                      value={nuevo.proveedor_nombre || ''}
                      onChange={(e) => setNuevo({ ...nuevo, proveedor_nombre: e.target.value, proveedor_id: '' })}
                      data-testid="input-nuevo-proveedor-libre"
                    />
                    <p className="text-[11px] text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      No hay proveedores creados. Crea uno en <a href="/crm/proveedores" target="_blank" className="underline">Proveedores</a> o escribe el nombre aquí (se enlazará si existe).
                    </p>
                  </div>
                ) : (
                  <Select
                    value={nuevo.proveedor_id}
                    onValueChange={(v) => setNuevo({ ...nuevo, proveedor_id: v })}
                  >
                    <SelectTrigger data-testid="select-nuevo-proveedor">
                      <SelectValue placeholder={`Selecciona proveedor (${proveedores.length} disponibles)`} />
                    </SelectTrigger>
                    <SelectContent>
                      {proveedores.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label>SKU del proveedor</Label>
                <Input
                  value={nuevo.sku_proveedor}
                  onChange={(e) => setNuevo({ ...nuevo, sku_proveedor: e.target.value })}
                  placeholder="opcional"
                />
              </div>
              <div>
                <Label>EAN / código barras</Label>
                <Input
                  value={nuevo.ean}
                  onChange={(e) => setNuevo({ ...nuevo, ean: e.target.value })}
                  placeholder="13 dígitos"
                />
              </div>
              <div>
                <Label>Precio compra (€)</Label>
                <Input
                  type="number" step="0.01"
                  value={nuevo.precio_compra}
                  onChange={(e) => setNuevo({ ...nuevo, precio_compra: e.target.value })}
                />
              </div>
              <div>
                <Label>Precio venta (€)</Label>
                <Input
                  type="number" step="0.01"
                  value={nuevo.precio_venta}
                  onChange={(e) => setNuevo({ ...nuevo, precio_venta: e.target.value })}
                />
              </div>
              <div>
                <Label>Cantidad para esta OT *</Label>
                <Input
                  type="number" min={1}
                  value={cantidadNuevo}
                  onChange={(e) => setCantidadNuevo(parseInt(e.target.value, 10) || 1)}
                  data-testid="input-cantidad-nuevo"
                />
              </div>
              <div>
                <Label>Stock mínimo</Label>
                <Input
                  type="number" min={0}
                  value={nuevo.stock_minimo}
                  onChange={(e) => setNuevo({ ...nuevo, stock_minimo: e.target.value })}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          {tab === 'buscar' ? (
            <Button onClick={submitBuscar} disabled={!selected || submitting} data-testid="btn-confirmar-buscar">
              {submitting ? 'Añadiendo…' : 'Añadir a la orden'}
            </Button>
          ) : (
            <Button onClick={submitCrear} disabled={submitting} data-testid="btn-confirmar-crear">
              {submitting ? 'Creando…' : 'Crear y añadir a la orden'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
